// import { getEnv, getJSON, getJSONForBillDiscounting }
import { getEnv, getJSON, getJSONForBillDiscounting } from 'components/AE/utils/FetchUtils';
import { handleErrorV2 } from '../handleError';
import { createContext, useContext, useEffect, useState } from 'react';
import { Role, Resource, CustomResource } from './type';
import humps from 'humps';
import qs from 'qs';
import Auth from 'utils/Auth';
import { toastr } from 'react-redux-toastr';
import { ErrorState } from 'typings';
import Logger from 'utils/Logger';
import { fetchUserGroup } from 'utils/Common';
import { CA_PRODUCT_CODES } from 'constants/AppConstants';
import { FetchUtils } from '@yubi/yb-module-auth';

type RoleState = {
    loading: boolean;
    loaded: boolean;
    roles: Role[];
    error: ErrorState;
};

type RoleStateParams = {
    loading?: boolean;
    loaded?: boolean;
    roles?: Role[];
    error?: ErrorState;
};

type ContextType = RoleState & {
    setRoleState(state: RoleStateParams): void;
    updateRole(role: Role): void;
    addRole(role: Role): void;
};

const Context = createContext<ContextType>({
    loading: false,
    loaded: false,
    roles: [],
    error: { code: 0, message: '' },
    setRoleState: () => ({}),
    updateRole: () => ({}),
    addRole: () => ({}),
});

export const RoleProvider: React.FC<any> = ({ children }) => {
    const [state, setRoleState] = useState<RoleState>({
        loading: false,
        loaded: false,
        roles: [],
        error: { code: 0, message: '' },
    });

    const updateRole = (roleToUpdate: Role) => {
        setRoleState((prevRoleState: RoleState) => ({
            ...prevRoleState,
            roles: prevRoleState.roles.map((role) =>
                role.id === roleToUpdate.id ? roleToUpdate : role
            ),
        }));
    };

    const addRole = (role: Role) => {
        setRoleState((prevRoleState: RoleState) => ({
            ...prevRoleState,
            roles: [...prevRoleState.roles, role],
        }));
    };

    const updateRoleState = (roleState: any) => {
        setRoleState((prevState: RoleState) => ({
            ...prevState,
            ...roleState,
        }));
    };

    return (
        <Context.Provider value={{ ...state, setRoleState: updateRoleState, updateRole, addRole }}>
            {children}
        </Context.Provider>
    );
};

type Props = {
    roleFetcher?(entityId: string, groupId: string, productId: string): Promise<Role[]>;
    productId: string;
};

const fetchRoles = (entityId: string, groupId: string, productId: string) => {
    const getJSONHandler =
        productId === CA_PRODUCT_CODES.BILL_DISCOUNTING ? getJSONForBillDiscounting : getJSON;
    return getJSONHandler(
        `${
            process.env.REACT_APP_AUTH_API_HOST
        }/entities/${entityId}/groups/${groupId}/roles?${qs.stringify(
            humps.decamelizeKeys({ productId, items: 1000 })
        )}`
    )
        .then((resp: any) => humps.camelizeKeys(resp) as any)
        .then((resp: Role[]) => resp)
        .catch((err) => {
            Logger.logException(err);
            return [];
        });
};

export const useRoles = ({ roleFetcher, productId }: Props) => {
    const { loaded, loading, roles, updateRole, addRole, setRoleState, error } =
        useContext(Context);
    useEffect(() => {
        if (!loaded && !loading) {
            const entityId = Auth.getEntityId();
            const group = fetchUserGroup((Auth as any).getActiveActor(), productId);
            const promise =
                typeof roleFetcher === 'function'
                    ? roleFetcher(entityId, group, productId)
                    : fetchRoles(entityId, group, productId);
            setRoleState({ loading: true, error: { code: 0, message: '' } });
            promise
                .then((roles: Role[]) => {
                    console.log('roles gb', roles);
                    setRoleState({ loading: false, loaded: true, roles });
                })
                .catch((err) => {
                    setRoleState({ loading: false, loaded: true, error: err });
                });
        }
    }, [loaded, loading, roleFetcher, setRoleState, productId]);
    return {
        loaded,
        loading,
        roles,
        updateRole,
        addRole,
        setRoleState,
        error,
    };
};

export const ResourceContext = createContext<{
    list: Resource[];
    loading: boolean;
    customResourceIsloading: boolean;
    customResourceList: CustomResource[];
}>({
    list: [],
    loading: false,
    customResourceIsloading: false,
    customResourceList: [],
});

export const ResourceProvider: React.FC<{
    children: React.ReactNode;
    productId: string;
}> = ({ children, productId }) => {
    const [resourceState, setResourceState] = useState<{
        list: Resource[];
        loading: boolean;
    }>({
        list: [],
        loading: false,
    });

    const [customResourceState, setCustomResourceState] = useState<{
        list: CustomResource[];
        loading: boolean;
    }>({
        list: [],
        loading: false,
    });

    useEffect(() => {
        /* const getJSONHandler =
      productId === CA_PRODUCT_CODES.BILL_DISCOUNTING
        ? getJSONForBillDiscounting
        : getJSON; */
        /* getJSONHandler(
      `${getEnv(
        process.env.REACT_APP_AUTH_API_HOST
      )}/products/${productId}/resources`
    ) */
        FetchUtils.getJSON(
            `${getEnv(process.env.REACT_APP_AUTH_API_HOST)}/products/${productId}/resources`
        )
            .then((resp: any) => humps.camelizeKeys(resp) as any)
            .then((resources: Resource[]) => {
                setResourceState(() => ({
                    loading: false,
                    list: resources,
                }));
                console.log('ssss ', resources);
            })
            .catch((err) => {
                console.log('qqqq ', err);
                handleErrorV2(err, 'Error in fetching reources and privileges', false).then(
                    ({ message, title, toastType = 'error' }: any) => {
                        const { [toastType]: toast }: any = toastr;
                        toast(title, message);
                    }
                );
                setResourceState(() => ({
                    loading: false,
                    list: [],
                }));
            });
    }, [productId]);

    useEffect(() => {
        /* const getJSONHandler =
      productId === CA_PRODUCT_CODES.BILL_DISCOUNTING
        ? getJSONForBillDiscounting
        : getJSON;
    getJSONHandler(
      `${getEnv(
        process.env.REACT_APP_AUTH_API_HOST
      )}/v3/entities/resources?product_id=${productId}`
    ) */
        FetchUtils.getJSON(
            `${getEnv(
                process.env.REACT_APP_AUTH_API_HOST
            )}/v3/entities/resources?product_id=${productId}`
        )
            .then((resp: any) => humps.camelizeKeys(resp) as any)
            .then((resources: any) => {
                setCustomResourceState(() => ({
                    loading: false,
                    list: resources.resources,
                }));
            })
            .catch((err) => {
                handleErrorV2(err, 'Error in fetching resources and privileges', false).then(
                    ({ message, title, toastType = 'error' }: any) => {
                        const { [toastType]: toast }: any = toastr;
                        toast(title, message);
                    }
                );
                setCustomResourceState(() => ({
                    loading: false,
                    list: [],
                }));
            });
    }, [productId]);
    return (
        <ResourceContext.Provider
            value={{
                loading: resourceState.loading,
                list: resourceState.list,
                customResourceIsloading: customResourceState.loading,
                customResourceList: customResourceState.list,
            }}
        >
            {children}
        </ResourceContext.Provider>
    );
};
